import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  FormHelperText,
  Typography,
  SelectChangeEvent
} from '@mui/material';
import { ContactFormData, Contact } from '../../types/contact';

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => void;
  contact?: Contact | null;
  isNew?: boolean;
}

interface ContactFormErrors {
  name?: string;
  type?: string;
  role?: string;
  target?: string;
  [key: string]: string | undefined;
}

const ContactForm: React.FC<ContactFormProps> = ({
  open,
  onClose,
  onSubmit,
  contact,
  isNew = true,
}) => {
  const initialFormData: ContactFormData = {
    name: '',
    type: 'email',
    target: '',
    role: '',
    is_active: true,
    description: '',
  };

  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (contact && !isNew) {
      setFormData({
        name: contact.name,
        type: contact.type,
        target: contact.target,
        role: contact.role,
        pool_id: contact.pool_id,
        is_active: contact.is_active,
        description: contact.description || '',
      });
    } else {
      setFormData(initialFormData);
    }
    // 폼이 열릴 때마다 에러 초기화
    setErrors({});
  }, [contact, isNew, open]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }> | SelectChangeEvent<unknown>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value,
      });
      // 오류 메시지 지우기
      if (errors[name]) {
        setErrors({
          ...errors,
          [name]: '',
        });
      }
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력';
    }

    if (!formData.role.trim()) {
      newErrors.role = '역할을 입력';
    }

    if (!formData.target.trim()) {
      newErrors.target = '연락처를 입력';
    } else {
      // 타입에 따른 형식 검증
      switch (formData.type) {
        case 'email':
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.target)) {
            newErrors.target = '올바른 이메일 형식 아님';
          }
          break;
        case 'sms':
          if (!/^\d{10,13}$/.test(formData.target.replace(/[^0-9]/g, ''))) {
            newErrors.target = '올바른 전화번호 형식 아님';
          }
          break;
        case 'webhook':
          if (!/^https?:\/\//.test(formData.target)) {
            newErrors.target = 'URL은 http:// 또는 https://로 시작해야';
          }
          break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isNew ? '담당자 추가' : '담당자 정보 수정'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="name"
              label="이름"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.type}>
              <InputLabel id="contact-type-label">연락 방법</InputLabel>
              <Select
                labelId="contact-type-label"
                name="type"
                value={formData.type}
                label="연락 방법"
                onChange={handleChange}
              >
                <MenuItem value="email">이메일</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="app">앱 알림</MenuItem>
                <MenuItem value="webhook">웹훅</MenuItem>
              </Select>
              {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="role"
              label="역할"
              value={formData.role}
              onChange={handleChange}
              error={!!errors.role}
              helperText={errors.role}
              required
              placeholder="담당 역할 (예: 관리자, 안전담당자)"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              name="target"
              label={
                formData.type === 'email'
                  ? '이메일 주소'
                  : formData.type === 'sms'
                  ? '전화번호'
                  : formData.type === 'webhook'
                  ? '웹훅 URL'
                  : '알림 대상'
              }
              value={formData.target}
              onChange={handleChange}
              error={!!errors.target}
              helperText={errors.target}
              required
              placeholder={
                formData.type === 'email'
                  ? 'user@example.com'
                  : formData.type === 'sms'
                  ? '010-1234-5678'
                  : formData.type === 'webhook'
                  ? 'https://example.com/webhook'
                  : ''
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              name="description"
              label="설명"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              placeholder="담당자에 대한 추가 설명"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label="활성화"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              비활성화된 담당자는 알림을 받지 않음
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {isNew ? '추가' : '수정'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactForm; 